import { Component } from '@theme/component';
import { oncePerEditorSession } from '@theme/utilities';

/**
 * Reveal progresivo al entrar en pantalla.
 *
 * Envuelve un grupo de elementos marcados con `data-reveal-item`. Cada uno
 * aparece cuando cruza el viewport, escalonado por su posición.
 *
 * El estado oculto vive detrás de `data-reveal="pending"`, atributo que sólo
 * pone este script. Si el módulo no carga o falla, el atributo nunca aparece y
 * el contenido se ve normal — nunca queda texto invisible por un error de JS.
 *
 * @extends {Component}
 */
class SoleuReveal extends Component {
  /** @type {IntersectionObserver | undefined} */
  #observer;

  connectedCallback() {
    super.connectedCallback();

    // El observer corre siempre: quien pidió menos movimiento igual recibe el
    // fundido de opacidad, que no dispara malestar vestibular. Es el CSS el que
    // decide qué se anima — el desplazamiento vertical queda detrás de una
    // media query `no-preference`.
    if (!('IntersectionObserver' in window)) return;

    const items = this.#items;
    if (items.length === 0) return;

    // Sin esta guarda, cada tecla en el personalizador re-dispara la entrada.
    oncePerEditorSession(this, `soleu-reveal-${this.dataset.revealKey ?? ''}`, () => {
      this.setAttribute('data-reveal', 'pending');

      for (const [i, item] of items.entries()) {
        item.style.setProperty('--soleu-reveal-index', String(i));
      }

      this.#observer = new IntersectionObserver(
        (entries) => {
          for (const entry of entries) {
            if (!entry.isIntersecting) continue;
            entry.target.setAttribute('data-reveal', 'in');
            this.#observer?.unobserve(entry.target);
          }
        },
        { threshold: 0.15, rootMargin: '0px 0px -8% 0px' }
      );

      for (const item of items) this.#observer.observe(item);
    });
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    // Sin esto, cada re-render del editor deja observers huérfanos acumulándose.
    this.#observer?.disconnect();
    this.#observer = undefined;
  }

  updatedCallback() {
    super.updatedCallback?.();
    // Tras un morph de la Section Rendering API el nodo se conserva pero los
    // hijos pueden ser otros: lo ya revelado se deja visible.
    if (this.getAttribute('data-reveal') !== 'pending') return;
    for (const item of this.#items) {
      if (!item.hasAttribute('data-reveal')) this.#observer?.observe(item);
    }
  }

  /** @returns {HTMLElement[]} */
  get #items() {
    return /** @type {HTMLElement[]} */ ([...this.querySelectorAll('[data-reveal-item]')]);
  }
}

if (!customElements.get('soleu-reveal-component')) {
  customElements.define('soleu-reveal-component', SoleuReveal);
}
